import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

interface SwitcherItem {
  id: string;
  reportType: "interaction";
  reportDate: string;
  locationCode: string | null;
  displayLabel: string;
  createdAt: string;
}

interface SwitcherResponse {
  items: SwitcherItem[];
}

interface UseBehaviorReportsForPickerOptions {
  locationFilter?: string[];
  searchText?: string;
  limit?: number;
}

interface UseBehaviorReportsForPickerResult {
  reports: SwitcherItem[];
  isLoading: boolean;
  error: Error | null;
}

export function useBehaviorReportsForPicker(
  options: UseBehaviorReportsForPickerOptions = {}
): UseBehaviorReportsForPickerResult {
  const { locationFilter = [], searchText = "", limit = 20 } = options;

  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const buildUrl = () => {
    const params = new URLSearchParams();
    if (debouncedSearch.trim()) {
      params.set("q", debouncedSearch.trim());
    }
    if (locationFilter.length === 1) {
      params.set("location", locationFilter[0]);
    }
    params.set("limit", String(Math.min(limit, 50)));
    const queryString = params.toString();
    return `/api/behavior/reports/switcher${queryString ? `?${queryString}` : ""}`;
  };

  const { data, isLoading, error } = useQuery<SwitcherResponse>({
    queryKey: ["/api/behavior/reports/switcher", { q: debouncedSearch, location: locationFilter.length === 1 ? locationFilter[0] : undefined, limit }],
    queryFn: async () => {
      const res = await fetch(buildUrl(), { credentials: "include" });
      if (!res.ok) {
        throw new Error("Failed to fetch behavior reports");
      }
      return res.json();
    },
    staleTime: 30_000,
  });

  let reports = data?.items || [];
  if (locationFilter.length > 1) {
    reports = reports.filter((r) => r.locationCode && locationFilter.includes(r.locationCode));
  }

  return {
    reports,
    isLoading,
    error: error as Error | null,
  };
}
